import { useState, useEffect, useCallback } from 'react';
import { fetchProviderTemplates, fetchHealthSystemTemplates, fetchGlobalTemplates, createProviderTemplate, updateProviderTemplate, deleteProviderTemplate, searchOtherProviderTemplates, getHealthSystemProviders } from '../../../../services/api';

export const useTemplates = (authData, activeCategory, providerInfo) => {
  
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const getApiToken = useCallback(() => {
    if (!authData) {
      throw new Error('I can\'t see your login info for some reason. Refresh the page or try logging in again (ERR SOL-42151)');
    }
    const token = authData.api_token || authData.access_token || authData.token;
    if (!token) {
      throw new Error('I can\'t see your login info for some reason. Refresh the page or try logging in again (ERR SOL-42160)');
    }
    return token;
  }, [authData]);

  const getProviderId = useCallback(() => {
    const providerId = providerInfo?.provider_id;
    if (!providerId) {
      throw new Error('I did not receive your unique ID from the server. Refresh the screen or try logging in again (ERR SOL-42192)');
    }
    return providerId;
  }, [providerInfo]);


  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const apiToken = getApiToken();
      let data = [];
      
      switch (activeCategory) {
        case 'provider':
          const providerId = getProviderId();
          const response = await fetchProviderTemplates(apiToken, providerId);
          // New API structure returns { success, provider_templates, health_system_templates }
          if (response.success && response.provider_templates) {
            data = response.provider_templates.templates || [];
          } else if (Array.isArray(response)) {
            // Fallback for old API structure
            data = response;
          }
          break;
        case 'health_system':
          const providerIdForHealthSystem = getProviderId();
          const healthSystemResponse = await fetchProviderTemplates(apiToken, providerIdForHealthSystem);
          // Use health system templates from the new API structure
          if (healthSystemResponse.success && healthSystemResponse.health_system_templates) {
            data = healthSystemResponse.health_system_templates.templates || [];
          }
          break;
        case 'global':
          data = await fetchGlobalTemplates(apiToken);
          break;
      }

      setTemplates(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(`Error fetching ${activeCategory} templates:`, err);
      setError(err.message || 'Failed to fetch templates');
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  }, [activeCategory, getApiToken, getProviderId]);

  const createTemplate = useCallback(async (templateData) => {
    const apiToken = getApiToken();
    const providerId = getProviderId();
    
    const result = await createProviderTemplate(apiToken, providerId, templateData);
    await fetchTemplates();
    return result;
  }, [getApiToken, getProviderId, fetchTemplates]);

  const updateTemplate = useCallback(async (templateId, updates) => {
    const apiToken = getApiToken();
    const providerId = getProviderId();
    
    const result = await updateProviderTemplate(apiToken, providerId, templateId, updates);
    await fetchTemplates();
    return result;
  }, [getApiToken, getProviderId, fetchTemplates]);

  const deleteTemplate = useCallback(async (templateId) => {
    const apiToken = getApiToken();
    const providerId = getProviderId();
    
    const result = await deleteProviderTemplate(apiToken, providerId, templateId);
    await fetchTemplates();
    return result;
  }, [getApiToken, getProviderId, fetchTemplates]);


  const searchProviderTemplates = useCallback(async (searchProviderId) => {
    const apiToken = getApiToken();
    const providerId = getProviderId();
    
    const result = await searchOtherProviderTemplates(apiToken, providerId, searchProviderId);
    return result;
  }, [getApiToken, getProviderId]);

  const fetchHealthSystemProviders = useCallback(async () => {
    const apiToken = getApiToken();
    const providerId = getProviderId();
    
    const result = await getHealthSystemProviders(apiToken, providerId);
    return result;
  }, [getApiToken, getProviderId]);

  const refreshTemplates = useCallback(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  return { templates, loading, error, refreshTemplates, createTemplate, updateTemplate, deleteTemplate, searchProviderTemplates, fetchHealthSystemProviders };
};